import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertCircle, CheckCircle, LockKeyholeCircle } from "@untitledui/icons";
import { useMemo, useState } from "react";
import z from "zod";
import { PasswordRequirementsChecklist } from "@/components/Atoms/PasswordRequirementsChecklist/PasswordRequirementsChecklist";
import { Button } from "@/components/base/buttons/button";
import { InputField, useAppForm } from "@/components/form";
import { createPasswordComplexitySchema } from "@/components/form/validation/create-common-validators";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";
import { HeaderNavigation } from "@/components/Molecules/HeaderNav/HeaderNav";
import type { AppTranslateFn } from "@/hooks/use-app-translation";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useDebounced } from "@/hooks/use-debounced";
import { useSetInitialPassword } from "@/services/auth";
import {
	evaluatePasswordRequirementFlags,
	PASSWORD_REQUIREMENT_RULE_ORDER,
	type PasswordRequirementRuleId,
	userKeys,
	userService,
} from "@/services/user";

// Search params schema for reset password link
const searchSchema = z.object({
	state: z.enum(["valid", "expired", "invalid", "error"]).catch("valid"),
	setPasswordToken: z.string().catch(""),
	userId: z.string().catch(""),
});

const createNewPasswordSchema = (t: AppTranslateFn) =>
	createPasswordComplexitySchema(t, 12);

const createPasswordFormSchema = (t: AppTranslateFn) =>
	z
		.object({
			newPassword: z.string().min(1, t("common.validation.passwordRequired")),
			confirmPassword: z
				.string()
				.min(1, t("common.validation.passwordConfirmRequired")),
		})
		.refine((data) => data.newPassword === data.confirmPassword, {
			message: t("common.validation.passwordsDoNotMatch"),
			path: ["confirmPassword"],
		});

type PasswordFormData = z.infer<ReturnType<typeof createPasswordFormSchema>>;

/** Checklist / UX hint only — API `validatePassword` decides accept/reject on submit. */
const SET_PASSWORD_CHECKLIST_MIN_LENGTH = 12;

/**
 * Subscribes to TanStack Form `newPassword` via field context — checklist updates on every keystroke.
 */
function SetPasswordNewPasswordRequirements({ value }: { value: string }) {
	const { t } = useAppTranslation();

	const items = useMemo(() => {
		const flags = evaluatePasswordRequirementFlags(
			value,
			SET_PASSWORD_CHECKLIST_MIN_LENGTH,
		);
		const labelFor = (id: PasswordRequirementRuleId): string => {
			switch (id) {
				case "minLength":
					return t("passwordRequirements.minLength", {
						count: SET_PASSWORD_CHECKLIST_MIN_LENGTH,
					});
				case "lowercase":
					return t("passwordRequirements.lowercase");
				case "uppercase":
					return t("passwordRequirements.uppercase");
				case "number":
					return t("passwordRequirements.number");
				case "symbol":
					return t("passwordRequirements.symbol");
			}
		};
		return PASSWORD_REQUIREMENT_RULE_ORDER.map((id) => ({
			id,
			label: labelFor(id),
			met: flags[id],
		}));
	}, [value, t]);

	return (
		<PasswordRequirementsChecklist
			className="mt-1"
			ariaLabel={t("passwordRequirements.listLabel")}
			items={items}
		/>
	);
}

function SetPasswordStrengthAndSubmit({
	userId,
	newPassword,
	confirmPassword,
	canSubmit,
	isSubmitting,
	isValidating,
	fieldMeta,
	submitValidationError,
}: {
	userId: string;
	newPassword: string;
	confirmPassword: string;
	canSubmit: boolean;
	isSubmitting: boolean;
	isValidating: boolean;
	fieldMeta: {
		newPassword?: { isValid?: boolean; isValidating?: boolean };
		confirmPassword?: { isValid?: boolean; isValidating?: boolean };
	};
	submitValidationError: string | null;
}) {
	const { t } = useAppTranslation();
	const newPasswordSchema = useMemo(() => createNewPasswordSchema(t), [t]);
	const debouncedPw = useDebounced(newPassword, 500);
	const { data: validation, isFetching } = useQuery({
		queryKey: userKeys.validatePassword(userId, debouncedPw),
		queryFn: () => userService.validatePassword(userId, debouncedPw),
		enabled: !!userId && newPasswordSchema.safeParse(debouncedPw).success,
	});

	const hasValues = !!newPassword && !!confirmPassword;
	const newMeta = fieldMeta.newPassword;
	const confirmMeta = fieldMeta.confirmPassword;
	const inSync = newPassword === debouncedPw;
	const apiOk = validation?.isValid === true;
	// Allow submit when debounced API says OK even before first blur (field meta
	// stays untouched until blurAsync). Confirm must still match and be valid.
	const newPasswordOk =
		newMeta?.isValid === true || (inSync && apiOk && !isFetching);
	const confirmOk = confirmMeta?.isValid === true && !confirmMeta?.isValidating;
	const fieldsAreValid =
		newPasswordOk && confirmOk && !newMeta?.isValidating && !isValidating;

	const isEnabled =
		canSubmit &&
		hasValues &&
		fieldsAreValid &&
		inSync &&
		apiOk &&
		!isFetching &&
		!isSubmitting &&
		!isValidating;

	return (
		<>
			{submitValidationError ? (
				<p className="text-sm text-fg-error-primary mt-2" role="alert">
					{submitValidationError}
				</p>
			) : null}
			<Button
				type="submit"
				color="primary"
				size="lg"
				className="w-full mt-6"
				isDisabled={!isEnabled}
			>
				{isSubmitting
					? t("common.actions.loading")
					: newMeta?.isValidating
						? t("setPassword.form.validating")
						: t("setPassword.form.submit")}
			</Button>
		</>
	);
}

export const Route = createFileRoute("/set-password")({
	validateSearch: searchSchema,
	component: ResetPasswordPage,
});

function ResetPasswordPage() {
	const { t } = useAppTranslation();
	const { state, setPasswordToken, userId } = Route.useSearch();
	const navigate = useNavigate();
	const setInitialPasswordMutation = useSetInitialPassword();

	const [submitState, setSubmitState] = useState<"idle" | "success" | "error">(
		"idle",
	);
	const [submitValidationError, setSubmitValidationError] = useState<
		string | null
	>(null);

	const newPasswordSchema = useMemo(() => createNewPasswordSchema(t), [t]);
	const passwordFormSchema = useMemo(() => createPasswordFormSchema(t), [t]);

	// Password strength: debounced query drives the checklist (below). Avoid
	// onChangeAsync + fetchQuery on this field — it reflows validation state and
	// can steal focus from the input on each keystroke.
	const newPasswordValidators = useMemo(
		() => ({
			onChange: ({ value }: { value: string }) => {
				if (!value) return undefined;
				const result = newPasswordSchema.safeParse(value);
				if (result.success) return undefined;
				return result.error.issues[0]?.message;
			},
			onBlurAsync: async ({ value }: { value: string }) => {
				// Only call API when all basic requirements are met
				if (!newPasswordSchema.safeParse(value).success) return undefined;
				const result = await userService.validatePassword(userId, value);
				if (result.isValid) return undefined;
				const msg = result.error?.trim();
				return msg || undefined;
			},
		}),
		[userId, newPasswordSchema],
	);

	const confirmPasswordValidators = useMemo(
		() => ({
			onChangeListenTo: ["newPassword"] as (
				| "newPassword"
				| "confirmPassword"
			)[],
			onChange: ({
				value,
				fieldApi,
			}: {
				value: string;
				fieldApi: {
					form: {
						getFieldValue: (n: "newPassword" | "confirmPassword") => string;
					};
				};
			}) => {
				if (!value) return undefined;
				const newPassword = fieldApi.form.getFieldValue("newPassword");
				return value === newPassword
					? undefined
					: t("common.validation.passwordsDoNotMatch");
			},
		}),
		[t],
	);

	const form = useAppForm({
		defaultValues: {
			newPassword: "",
			confirmPassword: "",
		} satisfies PasswordFormData,
		validators: {
			onSubmit: ({ value }: { value: PasswordFormData }) => {
				const result = passwordFormSchema.safeParse(value);
				if (!result.success) {
					const fields: Partial<Record<keyof PasswordFormData, string>> = {};
					for (const issue of result.error.issues) {
						const path = issue.path[0] as keyof PasswordFormData;
						if (path && !fields[path]) {
							fields[path] = issue.message;
						}
					}
					return { fields };
				}
				return undefined;
			},
		},
		onSubmit: async ({ value }: { value: PasswordFormData }) => {
			setSubmitValidationError(null);
			const apiCheck = await userService.validatePassword(
				userId,
				value.newPassword,
			);
			if (!apiCheck.isValid) {
				const msg = apiCheck.error?.trim();
				setSubmitValidationError(msg || null);
				return;
			}
			try {
				await setInitialPasswordMutation.mutateAsync({
					userId,
					data: {
						password: value.newPassword,
						token: setPasswordToken,
					},
				});
				setSubmitState("success");
			} catch (error) {
				console.error("Set initial password failed:", error);
				setSubmitState("error");
			}
		},
	});

	// Render success state
	if (submitState === "success") {
		return (
			<div className="flex flex-col min-h-screen overflow-hidden">
				<HeaderNavigation
					onUserProfile={() => {}}
					items={[]}
					name={""}
					email={""}
					onSignOut={() => {}}
					homeUrl={""}
					showAvatarDropdown={false}
				/>
				<div className="flex flex-1 items-center justify-center bg-background-primary px-6 py-10">
					<div className="w-full max-w-sm text-center">
						<div className="mb-6 flex justify-center">
							<FeaturedIcon
								icon={CheckCircle}
								color="success"
								theme="modern"
								size="xl"
							/>
						</div>
						<h1 className="mb-3 text-display-sm font-semibold text-fg-primary">
							{t("setPassword.success.title")}
						</h1>
						<p className="mb-8 text-md text-fg-secondary">
							{t("setPassword.success.description")}
						</p>
						<Button
							color="primary"
							size="lg"
							className="min-w-xs"
							onClick={() => navigate({ to: "/login" })}
						>
							{t("login.signIn")}
						</Button>
					</div>
				</div>
			</div>
		);
	}

	// Render submit error state
	if (submitState === "error") {
		return (
			<div className="flex flex-col min-h-screen overflow-hidden">
				<HeaderNavigation
					onUserProfile={() => {}}
					items={[]}
					name={""}
					email={""}
					onSignOut={() => {}}
					homeUrl={""}
					showAvatarDropdown={false}
				/>
				<div className="flex flex-1 items-center justify-center bg-background-primary px-6 py-10">
					<div className="w-full max-w-sm text-center">
						<div className="mb-6 flex justify-center">
							<FeaturedIcon
								icon={AlertCircle}
								color="error"
								theme="modern"
								size="xl"
							/>
						</div>
						<h1 className="mb-3 text-display-sm font-semibold text-fg-primary">
							{t("setPassword.submitError.title")}
						</h1>
						<p className="mb-8 text-md text-fg-secondary">
							{t("setPassword.submitError.description")}
						</p>
						<Button
							color="primary"
							size="lg"
							className="min-w-xs"
							onClick={() => setSubmitState("idle")}
						>
							{t("common.actions.tryAgain")}
						</Button>
					</div>
				</div>
			</div>
		);
	}

	// Render expired state
	if (state === "expired") {
		return (
			<div className="flex flex-col min-h-screen overflow-hidden">
				<HeaderNavigation
					onUserProfile={() => {}}
					items={[]}
					name={""}
					email={""}
					onSignOut={() => {}}
					homeUrl={""}
					showAvatarDropdown={false}
				/>
				<div className="flex flex-1 items-center justify-center bg-background-primary px-6 py-10">
					<div className="w-full max-w-sm text-center">
						<div className="mb-6 flex justify-center">
							<FeaturedIcon
								icon={AlertCircle}
								color="warning"
								theme="modern"
								size="xl"
							/>
						</div>
						<h1 className="mb-3 text-display-sm font-semibold text-fg-primary">
							{t("setPassword.expired.title")}
						</h1>
						<p className="mb-8 text-md text-fg-secondary">
							{t("setPassword.expired.description")}
						</p>
					</div>
				</div>
			</div>
		);
	}

	// Render invalid state
	if (state === "invalid") {
		return (
			<div className="flex flex-col min-h-screen overflow-hidden">
				<HeaderNavigation
					onUserProfile={() => {}}
					items={[]}
					name={""}
					email={""}
					onSignOut={() => {}}
					homeUrl={""}
					showAvatarDropdown={false}
				/>
				<div className="flex flex-1 items-center justify-center bg-background-primary px-6 py-10">
					<div className="w-full max-w-sm text-center">
						<div className="mb-6 flex justify-center">
							<FeaturedIcon
								icon={AlertCircle}
								color="error"
								theme="modern"
								size="xl"
							/>
						</div>
						<h1 className="mb-3 text-display-sm font-semibold text-fg-primary">
							{t("setPassword.invalid.title")}
						</h1>
						<p className="mb-8 text-md text-fg-secondary">
							{t("setPassword.invalid.description")}
						</p>
						<Button
							color="primary"
							size="lg"
							className="min-w-xs"
							onClick={() => navigate({ to: "/login" })}
						>
							{t("login.signIn")}
						</Button>
					</div>
				</div>
			</div>
		);
	}

	// Render error state (generic)
	if (state === "error") {
		return (
			<div className="flex flex-col min-h-screen overflow-hidden">
				<HeaderNavigation
					onUserProfile={() => {}}
					items={[]}
					name={""}
					email={""}
					onSignOut={() => {}}
					homeUrl={""}
					showAvatarDropdown={false}
				/>
				<div className="flex flex-1 items-center justify-center bg-background-primary px-6 py-10">
					<div className="w-full max-w-sm text-center">
						<div className="mb-6 flex justify-center">
							<FeaturedIcon
								icon={AlertCircle}
								color="error"
								theme="modern"
								size="xl"
							/>
						</div>
						<h1 className="mb-3 text-display-sm font-semibold text-fg-primary">
							{t("setPassword.error.title")}
						</h1>
						<p className="mb-8 text-md text-fg-secondary">
							{t("setPassword.error.description")}
						</p>
					</div>
				</div>
			</div>
		);
	}

	// Render valid state with form (default)
	return (
		<div className="flex flex-col min-h-screen overflow-hidden">
			<HeaderNavigation
				onUserProfile={() => {}}
				items={[]}
				name={""}
				email={""}
				onSignOut={() => {}}
			/>
			<div className="flex flex-1 items-center justify-center bg-background-primary px-6 py-10">
				<div className="w-full max-w-sm text-center">
					<div className="mb-6 flex justify-center">
						<FeaturedIcon
							icon={LockKeyholeCircle}
							color="brand"
							theme="modern"
							size="xl"
						/>
					</div>
					<h1 className="mb-3 text-display-sm font-semibold text-fg-primary">
						{t("setPassword.title")}
					</h1>
					<p className="mb-8 text-md text-fg-secondary">
						{t("setPassword.description")}
					</p>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							e.stopPropagation();
							form.handleSubmit();
						}}
						className="space-y-4 text-left"
					>
						<form.AppField
							name="newPassword"
							validators={newPasswordValidators}
						>
							{() => (
								<InputField
									type="password"
									label={t("resetPassword.form.newPassword")}
									placeholder={t("resetPassword.form.newPasswordPlaceholder")}
									autoComplete="new-password"
									data-testid="set-password-new-input"
								/>
							)}
						</form.AppField>

						<form.AppField
							name="confirmPassword"
							validators={confirmPasswordValidators}
						>
							{() => (
								<>
									<InputField
										type="password"
										label={t("resetPassword.form.confirmPassword")}
										placeholder={t(
											"resetPassword.form.confirmPasswordPlaceholder",
										)}
										autoComplete="new-password"
									/>
									<form.Subscribe
										selector={(state) => state.values.newPassword}
									>
										{(newPassword) => (
											<SetPasswordNewPasswordRequirements value={newPassword} />
										)}
									</form.Subscribe>
								</>
							)}
						</form.AppField>

						<form.Subscribe
							selector={(state) => ({
								canSubmit: state.canSubmit,
								isSubmitting: state.isSubmitting,
								isValidating: state.isValidating,
								values: state.values,
								fieldMeta: state.fieldMeta,
							})}
						>
							{({
								canSubmit,
								isSubmitting,
								isValidating,
								values,
								fieldMeta,
							}) => (
								<SetPasswordStrengthAndSubmit
									userId={userId}
									newPassword={values.newPassword}
									confirmPassword={values.confirmPassword}
									canSubmit={canSubmit}
									isSubmitting={isSubmitting}
									isValidating={isValidating}
									fieldMeta={fieldMeta}
									submitValidationError={submitValidationError}
								/>
							)}
						</form.Subscribe>
					</form>
				</div>
			</div>
		</div>
	);
}
